import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import { ActionsMenuEntry } from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import { useFeatureToggle } from "../external/ensolvers-core-frontend-mui/hooks/useFeatureToggle";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import DateUtils from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/DateUtils";
import { Documentation } from "../models/Documentation";
import Timeoff from "../models/Timeoff";
import DocumentationService from "../services/DocumentationService";
import PositionService from "../services/PositionService";
import ProjectService from "../services/ProjectService";
import { QueryKeyFactory } from "../services/queryKeys";
import timeoffService from "../services/TimeoffService";
import UserService from "../services/UserService";
import WorkLogService from "../services/WorkLogService";
import { TimeOffStatus } from "../utils/enums/TimeOffStatus";
import { Features } from "../utils/Features";
import OpsDateUtils from "../utils/OpsDateUtils";
import { unwrap } from "../utils/queryFn";
import RoleUtils from "../utils/RoleUtils";
import { createTableQuery, refreshTableQuery, TABLE_QUERY_STALE_TIME } from "../utils/tableQuery";
import TimeOffUtils from "../utils/TimeOffUtils";
import featureToggleList from "../utils/toggleConfig.json";
import { useHistory, useLocation } from "react-router-dom";
import { Routes } from "../types/Routes";
import { useTranslation } from "react-i18next";

import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import RuleIcon from "@mui/icons-material/Rule";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";
import { isPendingStatus } from "../components/TimeOff/TimeOffStatusChip";

const ONE_MEGABYTE = 1000000;

export enum TimeOffSortField {
  CREATED_AT = "createdAt",
  SHORT_START_DATE = "shortStartDate",
  SHORT_END_DATE = "shortEndDate",
}

function isTimeOffSortField(value: string): value is TimeOffSortField {
  return Object.values(TimeOffSortField).includes(value as TimeOffSortField);
}
const NO_NAMES: AutoCompleteUIDTO[] = [];
const timeoffKeys = new QueryKeyFactory(timeoffService.path);
const userKeys = new QueryKeyFactory(UserService.path);
const projectKeys = new QueryKeyFactory(ProjectService.path);
const positionKeys = new QueryKeyFactory(PositionService.path);

function withAdjustedDates(result: PagedResult<Timeoff>): PagedResult<Timeoff> {
  const content = result.content.map((item) => ({
    ...item,
    shortStartDate: OpsDateUtils.utcOffset(item.shortStartDate),
    shortEndDate: OpsDateUtils.utcOffset(item.shortEndDate),
  }));
  const pendingFirst = [...content].sort((a, b) => Number(isPendingStatus(b.status)) - Number(isPendingStatus(a.status)));
  return new PagedResult(pendingFirst, result.pageNumber, result.totalElements);
}

function sortPage(result: PagedResult<Timeoff>, sortBy?: string, sortDir?: string): PagedResult<Timeoff> {
  if (!sortBy || !isTimeOffSortField(sortBy)) return result;
  const sorted = [...result.content].sort((a, b) => {
    const valA = new Date(a[sortBy]).getTime();
    const valB = new Date(b[sortBy]).getTime();
    return sortDir === "asc" ? valA - valB : valB - valA;
  });
  return new PagedResult(sorted, result.pageNumber, result.totalElements);
}

const useTimeOffList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [timeoff, setTimeoff] = useState<Timeoff | undefined>();
  const [viewTimeoff, setViewTimeoff] = useState<Timeoff | undefined>();
  const [forceUpdateOn, setForceUpdateOn] = useState<number | undefined>(undefined);
  const [selectedTimeOff, setSelectedTimeOff] = useState<Timeoff | undefined>(undefined);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [dateFilter, setDateFilter] = useState<string>("Upcoming");
  const [selectedFiles, setSelectedFiles] = useState<Documentation[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [cancelRequestTimeOff, setCancelRequestTimeOff] = useState<Timeoff | undefined>(undefined);
  const [editTimeoff, setEditTimeoff] = useState<Timeoff | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [createForOthers, setCreateForOthers] = useState<boolean>(false);
  const [projectSearch, setProjectSearch] = useState<AutoCompleteUIDTO | null>(null);
  const [year, setYear] = useState<Date | null>(null);
  const [month, setMonth] = useState<Date | null>(null);
  const [isGanttGraphEnabled] = useFeatureToggle(featureToggleList, Features.GANTT_GRAPH);
  const isAdmin = RoleUtils.isAdmin();
  const isManager = RoleUtils.isManager();
  const userCanResolveRequest = RoleUtils.isAdminOrManager();
  const coreUserId = AuthService.getUser()?.externalId;

  const searchParams = new URLSearchParams(useLocation().search);
  const status = searchParams.get("status");
  const queryDate = searchParams.get("date");
  const queryYear = searchParams.get("year");
  const queryMonth = searchParams.get("month");
  const search = searchParams.get("search");
  const project = searchParams.get("project");
  const projectFilter = project || "All";

  const currentUserQuery = useQuery({
    queryKey: userKeys.detail(`core:${coreUserId ?? "anonymous"}`),
    queryFn: () => unwrap(UserService.findByCoreUserId(coreUserId!)),
    enabled: !!coreUserId,
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const userExternalId = currentUserQuery.data?.externalId;

  const adminProjectsQuery = useQuery({
    queryKey: projectKeys.detail("names"),
    queryFn: () => unwrap(ProjectService.findProjectNames()),
    enabled: isAdmin,
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const managerProjectsQuery = useQuery({
    queryKey: projectKeys.detail("manager-names"),
    queryFn: () => unwrap(ProjectService.findProjectsNamesByUserManagerExternalId()),
    enabled: isManager && !isAdmin,
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const projects = adminProjectsQuery.data ?? managerProjectsQuery.data ?? NO_NAMES;

  const adminUsersQuery = useQuery({
    queryKey: userKeys.detail("names"),
    queryFn: () => unwrap(UserService.findUserNames()),
    enabled: isAdmin,
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const managerUsersQuery = useQuery({
    queryKey: positionKeys.detail("manager-users"),
    queryFn: () => unwrap(PositionService.findProjectUserListFromManager()),
    enabled: isManager && !isAdmin,
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const usernames = adminUsersQuery.data ?? managerUsersQuery.data ?? NO_NAMES;

  const refreshTable = () => refreshTableQuery(timeoffKeys.lists(), () => setForceUpdateOn(Date.now()), queryClient);

  const loadTimeOffPage = async (page: number, pageSize: number, gantt: boolean, yearValue: Date | null, monthValue: Date | null) => {
    const pageResult = await unwrap(
      timeoffService.getTimeOff(
        page,
        pageSize,
        searchFilter,
        statusFilter,
        gantt ? "Upcoming" : dateFilter,
        projectFilter,
        yearValue?.getFullYear(),
        monthValue?.getMonth(),
      ),
    );
    return withAdjustedDates(pageResult);
  };

  const listKey = (page: number, pageSize: number, gantt: boolean, yearValue: Date | null, monthValue: Date | null) =>
    timeoffKeys.list({
      page,
      pageSize,
      searchFilter,
      statusFilter,
      dateFilter: gantt ? "Upcoming" : dateFilter,
      projectFilter,
      year: yearValue?.getFullYear() ?? "",
      month: monthValue == null ? "" : monthValue.getMonth(),
      gantt,
    });

  const onDataFetch = useMemo(
    () =>
      createTableQuery<Timeoff>({
        queryKey: (page, pageSize, sortBy, sortDir) =>
          timeoffKeys.list({
            page,
            pageSize,
            searchFilter,
            statusFilter,
            dateFilter,
            projectFilter,
            year: year?.getFullYear() ?? "",
            month: month == null ? "" : month.getMonth(),
            gantt: false,
            sortBy: sortBy ?? "",
            sortDir: sortDir ?? "",
          }),
        queryFn: async (page, pageSize, sortBy, sortDir) => {
          const base = await queryClient.fetchQuery({
            queryKey: listKey(page, pageSize, false, year, month),
            queryFn: () => loadTimeOffPage(page, pageSize, false, year, month),
            staleTime: TABLE_QUERY_STALE_TIME,
          });
          return sortPage(base, sortBy, sortDir);
        },
        client: queryClient,
      }),
    // listKey and loadTimeOffPage close over the same filters listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, searchFilter, statusFilter, dateFilter, projectFilter, year, month],
  );

  const getTimeOff = (
    page: number,
    pageSize: number,
    gantt: boolean = false,
    yearValue: Date | null = null,
    monthValue: Date | null = null,
  ) =>
    queryClient.fetchQuery({
      queryKey: listKey(page, pageSize, gantt, gantt ? null : yearValue, gantt ? null : monthValue),
      queryFn: () => loadTimeOffPage(page, pageSize, gantt, gantt ? null : yearValue, gantt ? null : monthValue),
      staleTime: TABLE_QUERY_STALE_TIME,
    });

  useEffect(() => {
    if (status === null) {
      searchParams.set("status", "All");
    } else {
      setStatusFilter(status);
    }

    if (queryDate === null) {
      searchParams.set("date", "Upcoming");
    } else {
      setDateFilter(queryDate);
    }

    const date = new Date();
    if (queryMonth !== null) {
      date.setMonth(parseInt(queryMonth) - 1);
    }
    if (queryYear !== null) {
      date.setFullYear(parseInt(queryYear));
    }
    if (queryMonth !== null) {
      setMonth(new Date(date.getTime()));
    }
    if (queryYear !== null) {
      setYear(new Date(date.getTime()));
    }

    if (Routes.TIME_OFF === window.location.pathname) history.replace({ pathname: Routes.TIME_OFF, search: searchParams.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!project) return;
    const queryProject = projects.find((item) => item.externalId === project);
    if (queryProject) setProjectSearch(queryProject);
  }, [project, projects]);

  const getUserWorkLogs = async (requesterExternalId: string, startDate: Date, endDate: Date) => {
    return unwrap(
      WorkLogService.findWorkLogsByRequesterAndDates(
        requesterExternalId,
        DateUtils.formatDate(startDate, "YYYY-MM-DD"),
        DateUtils.formatDate(endDate, "YYYY-MM-DD"),
      ),
    );
  };

  const warnIfWorklogsExist = async (requesterExternalId: string, startDate: Date, endDate: Date, successMessage: string) => {
    try {
      const response = await getUserWorkLogs(requesterExternalId, startDate, endDate);
      if (response.length > 0) {
        NotificationService.warn(
          "Operation successful. There is at least one worklog created for the same period. Please update it accordingly.",
        );
      } else {
        NotificationService.info(successMessage);
      }
    } catch {
      NotificationService.error("Error fetching user worklogs for selected days");
    }
  };

  const createMutation = useMutation({
    mutationFn: (timeOff: Timeoff) => unwrap(timeoffService.createTimeOff(timeOff, selectedFiles, coreUserId!)),
    onSuccess: async (_saved, timeOff) => {
      setSelectedFiles([]);
      await refreshTable();
      const requesterExternalId = timeOff.requesterExternalId ? timeOff.requesterExternalId : coreUserId!;
      await warnIfWorklogsExist(requesterExternalId, timeOff.shortStartDate, timeOff.shortEndDate, "TimeOff created successfully");
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ data, nextStatus }: { data: Timeoff; nextStatus: string }) => unwrap(timeoffService.changeStatus(data, nextStatus)),
  });

  const updateMutation = useMutation({
    mutationFn: (timeOff: Timeoff) => unwrap(timeoffService.updateTimeOff(timeOff, selectedFiles)),
    onSuccess: async (_saved, timeOff) => {
      setSelectedFiles([]);
      await refreshTable();
      await warnIfWorklogsExist(timeOff.requesterExternalId, timeOff.shortStartDate, timeOff.shortEndDate, "TimeOff updated successfully");
    },
  });

  const createTimeOff = async (timeOff: Timeoff) => {
    setIsLoading(true);
    timeOff.shortStartDate = OpsDateUtils.toUTCWithSetHours(timeOff.shortStartDate);
    timeOff.shortEndDate = OpsDateUtils.toUTCWithSetHours(timeOff.shortEndDate);
    try {
      await createMutation.mutateAsync(timeOff);
    } catch {
      // QueryClient already toasts ApiError from the failed create.
    } finally {
      setIsLoading(false);
    }
  };

  const updateTimeOff = async (timeOff: Timeoff) => {
    timeOff.status = timeOff.status.replaceAll(" ", "_");
    try {
      await updateMutation.mutateAsync(timeOff);
    } catch {
      // QueryClient already toasts ApiError from the failed update.
    }
  };

  const handleApprovalTimeOff = (data: Timeoff, status: string) => {
    if (!timeoff) return;
    timeoff.statusReason = data.statusReason;
    handleOnChangeTimeOffStatus(timeoff, status);
  };

  const handleOnCancelTimeOff = (data: Timeoff, status: string) => {
    if (!selectedTimeOff) return;
    selectedTimeOff.statusReason = data.statusReason;
    handleOnChangeTimeOffStatus(selectedTimeOff, status);
  };

  const clearStatusDialogs = () => {
    setTimeoff(undefined);
    setSelectedTimeOff(undefined);
    setCancelRequestTimeOff(undefined);
    setSelectedFiles([]);
  };

  const handleOnChangeTimeOffStatus = async (data: Timeoff, nextStatus: string) => {
    data.status = data.status.replaceAll(" ", "_");
    try {
      await statusMutation.mutateAsync({ data, nextStatus });
      clearStatusDialogs();
      if (nextStatus === TimeOffStatus.APPROVED) {
        TimeOffUtils.createTimeOffWorklogs(data);
      }
      if (nextStatus === TimeOffStatus.CANCELLED) {
        TimeOffUtils.deleteTimeOffWorklogs(data);
      }
      if (data.approvedBy && nextStatus === TimeOffStatus.PENDING) {
        TimeOffUtils.deleteTimeOffWorklogs(data);
      }
      if (nextStatus !== TimeOffStatus.CANCELLED && nextStatus !== TimeOffStatus.APPROVED) {
        NotificationService.info("Status changed successfully");
      }
      await refreshTable();
      await getUserWorkLogs(data.requesterExternalId, data.shortStartDate, data.shortEndDate).catch(() => undefined);
    } catch {
      clearStatusDialogs();
    }
  };

  const resetTimeOff = async (timeOff: Timeoff, nextStatus: string) => {
    const previousStatus = timeOff.status;
    timeOff.status = timeOff.status.replaceAll(" ", "_");
    try {
      await statusMutation.mutateAsync({ data: timeOff, nextStatus });
      setSelectedFiles([]);
      previousStatus === TimeOffStatus.APPROVED
        ? TimeOffUtils.deleteTimeOffWorklogs(timeOff)
        : NotificationService.info("Status reset successfully");
      await refreshTable();
    } catch {
      setSelectedFiles([]);
    }
  };

  const handleUploadDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    const fileList = e.target.files;

    if (!fileList) return;

    let files: Documentation[] = selectedFiles;
    let newDocument: Documentation;
    let fileToUpload: File = fileList[0];

    if (fileToUpload.size > ONE_MEGABYTE) {
      NotificationService.error("The maximum size to upload is 1MB.");
      setIsLoading(false);
      return;
    }

    (await DocumentationService.uploadDocument({ file: fileToUpload, name: fileToUpload.name }))
      .onSuccess((content) => {
        newDocument = content;
        files?.push(newDocument);
        setIsLoading(false);
      })
      .onError((message) => {
        NotificationService.error(message);
        setIsLoading(false);
      });

    setSelectedFiles(files);
    setIsLoading(false);
    await refreshTable();
  };

  const handleDeleteDocumentIcon = async (files: Documentation[]) => {
    setSelectedFiles(files);
    await refreshTable();
  };

  const getAdditionalActions = (instance: Timeoff): ActionsMenuEntry[] => {
    let options = Array<ActionsMenuEntry>();

    if (TimeOffUtils.userCanOpenOrCancel(instance, userExternalId) && TimeOffUtils.timeOffCanBeCancelled(instance)) {
      options.push({
        title: "Cancel",
        action: () => setSelectedTimeOff(instance),
      });
    }

    if (instance.status === TimeOffStatus.CANCELLED && TimeOffUtils.userCanOpenOrCancel(instance, userExternalId)) {
      options.push({
        title: "Reopen",
        action: () => setSelectedTimeOff(instance),
      });
    }

    if (
      RoleUtils.isAdminOrManager() &&
      TimeOffUtils.userCanChangeStatus(instance, userExternalId) &&
      instance.status.replace("_", " ") === TimeOffStatus.PENDING_CANCELLATION
    ) {
      options.push({
        title: "Approve / Reject",
        action: () => setCancelRequestTimeOff(instance),
      });
    }

    if (TimeOffUtils.userCanChangeStatus(instance, userExternalId)) {
      options.push(
        instance.status === TimeOffStatus.PENDING || instance.status.replace("_", " ") === TimeOffStatus.ADD_DOCUMENTATION
          ? {
              title: "Approve / Reject",
              action: () => setTimeoff(instance),
            }
          : {
              title: "Reset status",
              action: () => resetTimeOff(instance, TimeOffStatus.PENDING),
            },
      );
    }

    if (TimeOffUtils.timeOffCanBeEdited(instance)) {
      options.push({
        title: "Edit",
        action: () => openEditDialog(instance),
      });
    }
    return options;
  };

  const handleMissingDocumentationRequested = (instance: Timeoff) => {
    return instance.status.replace("_", " ") === TimeOffStatus.ADD_DOCUMENTATION && instance.documentation.length === 0;
  };

  const openViewDialog = (instance: Timeoff) => {
    setViewTimeoff(instance);
    setSelectedFiles(instance.documentation);
  };

  const openEditDialog = (instance: Timeoff) => {
    setEditTimeoff(instance);
    setSelectedFiles(instance.documentation);
  };

  const closeViewDialog = () => {
    setSelectedFiles([]);
    setViewTimeoff(undefined);
  };

  const closeEditDialog = () => {
    setSelectedFiles([]);
    setEditTimeoff(undefined);
  };

  const clearSelectedFiles = () => setSelectedFiles([]);
  const clearTimeoff = () => setTimeoff(undefined);
  const clearSelectedTimeoff = () => setSelectedTimeOff(undefined);

  const getAdditionalActionsIcons = (instance: Timeoff): ActionsMenuEntryIcons[] => {
    const actions = [];

    if (TimeOffUtils.userCanOpenOrCancel(instance, userExternalId) && TimeOffUtils.timeOffCanBeCancelled(instance)) {
      actions.push({
        title: t("Cancel"),
        action: () => setSelectedTimeOff(instance),
        icon: <CancelIcon />,
      });
    }
    if (instance.status === TimeOffStatus.CANCELLED && TimeOffUtils.userCanOpenOrCancel(instance, userExternalId)) {
      actions.push({
        title: t("Reopen"),
        action: () => setSelectedTimeOff(instance),
        icon: <EventRepeatIcon />,
      });
    }

    if (
      RoleUtils.isAdminOrManager() &&
      TimeOffUtils.userCanChangeStatus(instance, userExternalId) &&
      instance.status.replace("_", " ") === TimeOffStatus.PENDING_CANCELLATION
    ) {
      actions.push({
        title: "Approve / Reject",
        action: () => setCancelRequestTimeOff(instance),
        icon: <RuleIcon />,
      });
    }

    if (TimeOffUtils.userCanChangeStatus(instance, userExternalId)) {
      actions.push(
        {
          title: "Approve / Reject",
          action: () => setTimeoff(instance),
          icon: <RuleIcon />,
        },
        {
          title: "Reset status",
          action: () => resetTimeOff(instance, TimeOffStatus.PENDING),
          icon: <RestartAltIcon />,
        },
      );
    }

    if (TimeOffUtils.timeOffCanBeEdited(instance)) {
      actions.push({
        title: t("Edit"),
        action: () => openEditDialog(instance),
        icon: <EditIcon />,
      });
    }
    actions.push({
      title: t("View"),
      action: () => openEditDialog(instance),
      icon: <VisibilityIcon />,
    });
    return actions;
  };

  return {
    timeoffState: {
      timeoff,
      getTimeOff,
      createTimeOff,
      clearTimeoff,
      updateTimeOff,
      selectedTimeOff,
      editTimeoff,
      clearSelectedTimeoff,
      viewTimeoff,
      handleOnChangeTimeOffStatus,
      cancelRequestTimeOff,
      setCancelRequestTimeOff,
      handleApprovalTimeOff,
      handleOnCancelTimeOff,
    },
    clearSelectedFiles,
    handleUploadDocument,
    handleDeleteDocumentIcon,
    getAdditionalActions,
    getAdditionalActionsIcons,
    openViewDialog,
    closeViewDialog,
    closeEditDialog,
    setSelectedFiles,
    isEditing,
    usernames,
    userExternalId,
    selectedFiles,
    isLoading,
    openCreateModal,
    setOpenCreateModal,
    createForOthers,
    setCreateForOthers,
    userCanResolveRequest,
    setIsEditing,
    onDataFetch,
    forceUpdateOn,
    year,
    setYear,
    month,
    setMonth,
    searchFilter,
    statusFilter,
    dateFilter,
    projects,
    projectSearch,
    setSearchFilter,
    setStatusFilter,
    setDateFilter,
    setProjectSearch,
    getUserWorkLogs,
    handleMissingDocumentationRequested,
    isGanttGraphEnabled,
    handleOnChangeTimeOffStatus,
    status,
    queryDate,
    queryYear,
    queryMonth,
    search,
    project,
  };
};

export default useTimeOffList;
